import { useTranslation } from 'react-i18next'
import {
	Accordion, AccordionContent, AccordionItem, AccordionTrigger,
	Button,
} from '@rentflowdev/ui'
import { DEFAULT_LOCALE } from '@/types'
import type { RentalAutomation } from '@/types'
import { useGetRentalAutomationQuery } from '@/api'

const ACCORDION_SECTIONS = ['accruals', 'reminders', 'payments', 'utilities', 'completion'] as const

function formatShortDate(iso?: string): string {
	if (!iso) return '—'
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })
}

interface RentalAutomationSectionProps {
	rentalId: string
	/** Opens the upsert dialog. Owned by the parent route so the same dialog is reused for both setup and edit. */
	onSetup?: () => void
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<>
			<span className="text-sm text-(--color-muted-foreground)">{label}:</span>
			<span className="text-sm text-(--color-foreground)">{value}</span>
		</>
	)
}

function AccrualsFields({ a, t }: { a: RentalAutomation; t: (k: string, opts?: Record<string, unknown>) => string }) {
	const invoiceFormationValue =
		a.invoiceFormation === 'DAYS_BEFORE_PAYMENT' && a.invoiceFormationDaysBefore != null
			? t('rent.automation.values.daysBeforePayment', { count: a.invoiceFormationDaysBefore, defaultValue: `за ${a.invoiceFormationDaysBefore} дня до оплаты` })
			: t(`rent.automation.invoiceFormation.${a.invoiceFormation}`)

	return (
		<div className="grid grid-cols-[240px_1fr] gap-x-4 gap-y-3">
			<Field label={t('rent.automation.fields.paymentDay')} value={`${a.paymentDay}-е число`} />
			<Field label={t('rent.automation.fields.invoiceFormation')} value={invoiceFormationValue} />
			<Field label={t('rent.automation.fields.sendToTenant')} value={t(`rent.automation.sendMode.${a.sendToTenantMode}`)} />
			<Field label={t('rent.automation.fields.actFormation')} value={t(`rent.automation.actFormation.${a.actFormation}`)} />
			<Field label={t('rent.automation.fields.accrualDistribution')} value={t(`rent.automation.accrualDistribution.${a.accrualDistribution}`)} />
		</div>
	)
}

function RemindersFields({ a, t }: { a: RentalAutomation; t: (k: string, opts?: Record<string, unknown>) => string }) {
	return (
		<div className="grid grid-cols-[240px_1fr] gap-x-4 gap-y-3">
			<Field
				label={t('rent.automation.fields.reminderBefore')}
				value={a.reminderBeforeDaysBefore != null
					? t('rent.automation.values.daysBefore', { count: a.reminderBeforeDaysBefore, defaultValue: `за ${a.reminderBeforeDaysBefore} дня до оплаты` })
					: '—'}
			/>
			<Field label={t('rent.automation.fields.remindOnDueDate')} value={a.remindOnDueDate ? t('common.yes') : t('common.no')} />
			<Field
				label={t('rent.automation.fields.repeatReminder')}
				value={a.repeatReminderEveryDays != null && a.repeatReminderMaxTimes != null
					? t('rent.automation.values.repeat', {
						days: a.repeatReminderEveryDays,
						max: a.repeatReminderMaxTimes,
						defaultValue: `Каждые ${a.repeatReminderEveryDays} дня, максимум ${a.repeatReminderMaxTimes} раза`,
					})
					: '—'}
			/>
		</div>
	)
}

function PaymentsFields({ a, t }: { a: RentalAutomation; t: (k: string, opts?: Record<string, unknown>) => string }) {
	return (
		<div className="grid grid-cols-[240px_1fr] gap-x-4 gap-y-3">
			<Field label={t('rent.automation.fields.paymentDistribution')} value={t(`rent.automation.paymentDistribution.${a.paymentDistribution}`)} />
		</div>
	)
}

function UtilitiesFields({ a, t }: { a: RentalAutomation; t: (k: string, opts?: Record<string, unknown>) => string }) {
	return (
		<div className="grid grid-cols-[240px_1fr] gap-x-4 gap-y-3">
			<Field
				label={t('rent.automation.fields.readingsRequestBefore')}
				value={a.utilityReadingsRequestDaysBefore != null
					? t('rent.automation.values.daysBeforeCalc', { count: a.utilityReadingsRequestDaysBefore, defaultValue: `За ${a.utilityReadingsRequestDaysBefore} дня до расчета` })
					: '—'}
			/>
			<Field label={t('rent.automation.fields.remindOnCalculationDay')} value={a.utilityRemindOnCalculationDay ? t('common.yes') : t('common.no')} />
		</div>
	)
}

function CompletionFields({ a, t }: { a: RentalAutomation; t: (k: string, opts?: Record<string, unknown>) => string }) {
	return (
		<div className="grid grid-cols-[240px_1fr] gap-x-4 gap-y-3">
			<Field
				label={t('rent.automation.fields.completionReminder')}
				value={a.completionReminderDaysBefore != null
					? t('rent.automation.values.daysBeforeEnd', { count: a.completionReminderDaysBefore, defaultValue: `За ${a.completionReminderDaysBefore} дней до окончания` })
					: '—'}
			/>
		</div>
	)
}

export function RentalAutomationSection({ rentalId, onSetup }: RentalAutomationSectionProps) {
	const { t } = useTranslation()

	const { data } = useGetRentalAutomationQuery(rentalId)
	const automation: RentalAutomation | undefined = data ?? undefined

	if (!automation) {
		return (
			<div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
				<img
					src="/logos/Illustration_Empty.png"
					alt=""
					aria-hidden="true"
					className="h-32 w-auto select-none"
				/>
				<div className="flex flex-col items-center gap-1 text-center">
					<h3 className="text-base font-[500] text-(--color-foreground)">
						{t('rent.automation.emptyTitle')}
					</h3>
					<p className="max-w-sm text-sm text-(--color-muted-foreground)">
						{t('rent.automation.emptySubtitle')}
					</p>
				</div>
				<Button onClick={onSetup} disabled={!onSetup}>
					{t('rent.automation.setup')}
				</Button>
			</div>
		)
	}

	// suppress unused warning when backend ships dates via automation.sendToTenantAt etc.
	void formatShortDate

	return (
		<Accordion type="multiple" defaultValue={[...ACCORDION_SECTIONS]} className="flex flex-col gap-3">
			<AccordionItem value="accruals" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.automation.sections.accruals')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					<AccrualsFields a={automation} t={t} />
				</AccordionContent>
			</AccordionItem>

			<AccordionItem value="reminders" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.automation.sections.reminders')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					<RemindersFields a={automation} t={t} />
				</AccordionContent>
			</AccordionItem>

			<AccordionItem value="payments" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.automation.sections.payments')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					<PaymentsFields a={automation} t={t} />
				</AccordionContent>
			</AccordionItem>

			<AccordionItem value="utilities" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.automation.sections.utilities')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					<UtilitiesFields a={automation} t={t} />
				</AccordionContent>
			</AccordionItem>

			<AccordionItem value="completion" className="rounded-(--radius-md) border border-(--color-border) px-4">
				<AccordionTrigger className="py-4 text-base font-[500]">
					{t('rent.automation.sections.completion')}
				</AccordionTrigger>
				<AccordionContent className="pb-4">
					<CompletionFields a={automation} t={t} />
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}
